import { Chip } from '@mui/material';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useFormik } from 'formik';
import Papa from 'papaparse';
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Table,
} from 'reactstrap';
import * as Yup from 'yup';
import { getWarehouseListReq } from '../../service/branchService';
import StyledButton from '../Common/StyledButton';

import { toast } from 'react-toastify';
import { createBulkBranch, getBudgets } from '../../api';
import ActionComponent from './ActionComponent';
import AddBranch from './AddBranch';

const BranchData = (props) => {
  const {
    branchData,
    loading,
    handleSubmit,
    clientId,
    openModal,
    setOpenModal,
    handleToggle,
    taxIdentities,
  } = props;

  const [edit, setEdit] = useState(null);
  const [budgetData, setBudgetData] = useState({
    openModal: false,
    data: [],
  });
  const [warehouseList, setWarehouseList] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [failedData, setFailedData] = useState({});
  const [openFailedModal, setOpenFailedModal] = useState(false);

  const searchAllWareHouses = async () => {
    try {
      const response = await getWarehouseListReq();
      setWarehouseList(response?.payload?.warehouses);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    searchAllWareHouses();
  }, []);

  //For creating new Branch need Formik for validation schema
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      primaryBranch: {
        name: null,
        associatedWarehouse: null,
        contact: null,
        code: null,
        zohoAddressId: null,
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pincode: '',
        gstin: '',
        pan: '',
      },
    },
    validationSchema: Yup.object({
      primaryBranch: Yup.object().shape({
        name: Yup.string().required('Please Enter Branch Name'),
        associatedWarehouse: Yup.string().required(
          'Please Select Associated Warehouse',
        ),
        contact: Yup.string().required('Please Enter Valid Contact Number'),
        code: Yup.string()
          .required('Please Enter Branch Code')
          .max(10, 'Branch Code cannot exceed 10 character'),
        zohoAddressId: Yup.string()
          .matches(
            /^\d{10,20}$/,
            'Zoho Address ID must be a 10–20 digit number',
          )
          .required('Please Enter Zoho Address Id'),
        gstin: Yup.string().required('GST number is required'),
        pan: Yup.string().required('PAN number is required'),
        addressLine1: Yup.string().required('Address Line 1 is required'),
        addressLine2: Yup.string(),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        pincode: Yup.string()
          .matches(
            /^[1-9][0-9]{5}$/,
            'Pincode must be a valid 6-digit number starting with 1–9',
          )
          .required('Pincode is required'),
      }),
    }),
    onSubmit: (values) => {
      const newBranch = {
        ...values.primaryBranch,
        clientId,
      };
      handleSubmit(newBranch, edit);
    },
  });

  const notify = (type, message) => {
    if (type === 'Error') {
      toast.error(message, {
        position: 'top-center',
        theme: 'colored',
      });
    } else {
      toast.success(message, {
        position: 'top-center',
        theme: 'colored',
      });
    }
  };

  const BulkAddBranch = async (data) => {
    try {
      const updatedData = data.map((ele) => {
        const {
          Name,
          Code,
          GST,
          PAN,
          AddressLine1,
          AddressLine2,
          City,
          State,
          Pincode,
          Contact,
        } = ele;

        return {
          name: Name,
          code: Code,
          addressLine1: AddressLine1,
          addressLine2: AddressLine2,
          city: City,
          state: State,
          gstin: GST,
          pan: PAN,
          pincode: parseInt(Pincode, 10),
          zohoAddressId: ele['Zoho AddressId'],
          contact: Contact,
          associatedWarehouse: ele['Associated WH'],
        };
      });
      const response = await createBulkBranch({
        clientId,
        branches: updatedData,
      });
      if (response.success) {
        const {
          payload: { createdCount, failedCount, failedRows },
        } = response;
        if (failedCount > 0) {
          setFailedData(failedRows);
          setOpenFailedModal(true);
        } else {
          notify('success', `Created (${createdCount}) Branches`);
        }
      } else {
        notify('Error', response.message);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleFileUpload = async (event) => {
    setIsUploading(true);
    const file = event.target.files[0];

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        BulkAddBranch(results.data);
        fileInputRef.current.value = null;
        setIsUploading(false);
      },
      error: function (err) {
        console.error('Error parsing CSV:', err);
        setIsUploading(false);
      },
    });
  };

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const onViewBudgetClick = async (clientId, branchId) => {
    const response = await getBudgets({ clientId, branchId });
    setBudgetData({
      openModal: true,
      data: response.payload.budgets,
    });
  };

  const getBudgetModal = () => {
    const { openModal, data } = budgetData;
    return (
      <Modal
        size="md"
        centered
        isOpen={openModal}
        toggle={() => {
          setBudgetData({
            openModal: false,
            data: [],
          });
        }}
      >
        <div className="modal-header mb-2">
          <h4>Budget Details</h4>
          <StyledButton
            outline
            color="danger"
            onClick={() =>
              setBudgetData({
                openModal: false,
                data: [],
              })
            }
          >
            Close
          </StyledButton>
        </div>
        <ModalBody>
          {data.length > 0 ? (
            <>
              <h5>Branch Name: {data?.[0]?.branchId.name}</h5>
              <table className="table table-bordered align-middle mt-3">
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>Fiscal Year</th>
                    <th style={{ width: '30%' }}>Type</th>
                    <th style={{ width: '50%' }}>Amount(₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.map((budget, index) => (
                    <tr key={index}>
                      <td>{`${budget.fiscalYear}-${parseInt(budget.fiscalYear, 10) + 1}`}</td>
                      <td>{budget.period.toUpperCase()}</td>
                      <td>{budget.amount}/-</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            'No Budget Set'
          )}
        </ModalBody>
      </Modal>
    );
  };

  return (
    <div>
      <Modal
        size="lg"
        isOpen={openModal.branch}
        toggle={() => {
          handleToggle('branch');
        }}
      >
        <div>
          <Form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            <div className="modal-header">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h5 className="modal-title">{edit ? 'Edit' : 'Add'} Branch</h5>
                <div className="d-flex gap-1">
                  <Button
                    color="primary"
                    outline
                    onClick={() => {
                      setOpenModal({ ...openModal, branch: false });
                    }}
                  >
                    Close
                  </Button>
                  <Button color="primary" type="submit">
                    Save
                  </Button>
                </div>
              </div>
            </div>
            <ModalBody>
              <AddBranch
                validation={validation}
                warehouseList={warehouseList}
                taxIdentities={taxIdentities}
              />
            </ModalBody>
          </Form>
        </div>
      </Modal>
      <div style={{ maxHeight: 300, width: '100%', overflow: 'auto' }}>
        <Table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Associated Warehouse</th>
              <th colSpan={2}>Contact</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  <Spinner />
                  <h5 className="m-2">Loading...</h5>
                </td>
              </tr>
            ) : (
              <>
                {branchData.length > 0 ? (
                  branchData.map((branch) => (
                    <tr key={branch._id} style={{ verticalAlign: 'middle' }}>
                      <td>
                        {branch.Name}{' '}
                        <span>
                          {' '}
                          {branch.isPrimary ? (
                            <Chip className="mx-1" size="sm" label="Primary" />
                          ) : null}
                        </span>
                      </td>
                      <td>{branch.AssociatedWarehouse}</td>
                      <td>{branch.Contact}</td>
                      <td>
                        <ActionComponent
                          openModal={openModal}
                          setOpenModal={setOpenModal}
                          type={'branch'}
                          data={branch}
                          clientId={clientId}
                          validation={validation}
                          setEdit={setEdit}
                          onViewBudgetClick={onViewBudgetClick}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center' }}>
                      No Rows to Show
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex my-3 justify-content-between align-items-center">
        <div>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />

          <StyledButton
            color="primary"
            className="w-md"
            onClick={handleButtonClick}
            isLoading={isUploading}
          >
            <i className="ion ion-md-cloud-download mx-2"></i>
            Bulk Import
          </StyledButton>
        </div>

        <Button
          onClick={() => {
            setOpenModal({ ...openModal, branch: true });
            setEdit(null);
            validation.resetForm();
          }}
          color="primary"
        >
          Add Branch
        </Button>
      </div>
      <Modal
        size="md"
        centered
        isOpen={openFailedModal}
        toggle={() => {
          setOpenFailedModal(false);
        }}
      >
        <ModalBody>
          <pre>{JSON.stringify(failedData, null, 4)}</pre>
        </ModalBody>
      </Modal>
      {getBudgetModal()}
    </div>
  );
};

export default BranchData;
